import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listWarrantyLeads, accessWarrantyLead } from "@/Calls/callWarrantyAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Car, AlertCircle } from "lucide-react";

const STATUS_COLOR: Record<string, string> = {
    OPEN:     "bg-blue-100 text-blue-700",
    ACCEPTED: "bg-green-100 text-green-700",
    CLOSED:   "bg-gray-100 text-gray-600",
};

interface LeadRequest {
    request_id: number;
    vehicle_vin: string | null;
    vehicle_year: number | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_type: string | null;
    mileage: string | null;
    consent_at: string | null;
    created_at: string;
    status: string;
    lead_accesses: { access_id: number }[];
}

interface PulledData {
    customer: {
        customer_fname: string | null; customer_lname: string | null;
        customer_email: string | null; customer_phoneno: string | null;
        customer_home_addr1: string | null; customer_home_city: string | null;
        customer_home_state: string | null; customer_home_zip: string | null;
        customer_dob: string | null;
    };
    request: LeadRequest;
    deal: any;
}

export default function WarrantyQuoteRequestsPage() {
    const navigate  = useNavigate();
    const [requests,    setRequests]    = useState<LeadRequest[]>([]);
    const [loading,     setLoading]     = useState(true);
    const [pulling,     setPulling]     = useState<number | null>(null);
    const [confirmId,   setConfirmId]   = useState<number | null>(null);
    const [pulledData,  setPulledData]  = useState<Record<number, PulledData>>({});
    const [error,       setError]       = useState<string | null>(null);

    useEffect(() => {
        listWarrantyLeads()
            .then((r) => setRequests(r.data.requests ?? []))
            .catch(() => setRequests([]))
            .finally(() => setLoading(false));
    }, []);

    const handlePullLead = async (requestId: number) => {
        setPulling(requestId);
        setConfirmId(null);
        setError(null);
        try {
            const res = await accessWarrantyLead(requestId);
            if (res.data.ok) {
                setPulledData((prev) => ({ ...prev, [requestId]: res.data }));
                setRequests((prev) => prev.map((r) =>
                    r.request_id === requestId
                        ? { ...r, lead_accesses: [{ access_id: res.data.access.access_id }] }
                        : r
                ));
            }
        } catch (e: any) {
            setError(e.response?.data?.message ?? "Failed to pull lead");
        } finally {
            setPulling(null);
        }
    };

    if (loading) return (
        <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Quote Requests</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {requests.length} open lead{requests.length !== 1 ? "s" : ""} matching your coverage
                </p>
            </div>

            {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            )}

            {requests.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <ShieldCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No leads available right now</p>
                    <p className="text-sm text-muted-foreground mt-1">Check back as customers request warranty coverage</p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {requests.map((req) => {
                        const alreadyPulled = req.lead_accesses.length > 0;
                        const data = pulledData[req.request_id];
                        const vinDisplay = alreadyPulled && data
                            ? req.vehicle_vin ?? "—"
                            : req.vehicle_vin ? `****${req.vehicle_vin.slice(-4)}` : "—";

                        return (
                            <Card key={req.request_id} className="p-5 space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Car className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-medium">
                                                {req.vehicle_year} {req.vehicle_make} {req.vehicle_model}
                                            </span>
                                            {req.vehicle_type && (
                                                <span className="text-xs bg-slate-100 text-slate-600 rounded px-2 py-0.5">
                                                    {req.vehicle_type}
                                                </span>
                                            )}
                                            <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${STATUS_COLOR[req.status] ?? "bg-gray-100"}`}>
                                                {req.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground font-mono">VIN: {vinDisplay}</p>
                                        {req.mileage && (
                                            <p className="text-sm text-muted-foreground">Mileage: {req.mileage}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            Requested {new Date(req.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="shrink-0">
                                        {!alreadyPulled && req.status === "OPEN" ? (
                                            confirmId === req.request_id ? (
                                                <div className="flex items-center gap-2">
                                                    <p className="text-xs text-muted-foreground">Charge $25?</p>
                                                    <Button
                                                        size="sm"
                                                        className="bg-purple-600 text-white hover:bg-purple-700 text-xs"
                                                        disabled={pulling === req.request_id}
                                                        onClick={() => handlePullLead(req.request_id)}
                                                    >
                                                        {pulling === req.request_id
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : "Confirm"
                                                        }
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => setConfirmId(null)}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    className="bg-purple-600 text-white hover:bg-purple-700"
                                                    onClick={() => setConfirmId(req.request_id)}
                                                >
                                                    Pull Lead ($25)
                                                </Button>
                                            )
                                        ) : alreadyPulled ? (
                                            <span className="text-xs text-green-600 font-medium">Lead Accessed ✓</span>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Revealed customer data after pull */}
                                {alreadyPulled && data && (
                                    <div className="border-t pt-3 grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Customer</p>
                                            <p className="font-medium">{data.customer.customer_fname} {data.customer.customer_lname}</p>
                                            <p className="text-muted-foreground">{data.customer.customer_email}</p>
                                            <p className="text-muted-foreground">{data.customer.customer_phoneno}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Address</p>
                                            <p className="text-muted-foreground">{data.customer.customer_home_addr1}</p>
                                            <p className="text-muted-foreground">
                                                {data.customer.customer_home_city}, {data.customer.customer_home_state} {data.customer.customer_home_zip}
                                            </p>
                                        </div>
                                        <div className="col-span-2">
                                            <Button
                                                size="sm"
                                                className="bg-indigo-600 text-white hover:bg-indigo-700"
                                                onClick={() => navigate(`/warranty/quote-requests/${req.request_id}/submit`)}
                                            >
                                                Submit Coverage Quote
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
